import * as THREE from 'three'
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { computeDefaultOffset } from '@/utils/cameraFit'
import { easeInOut } from '@/utils/pose'

export type Viewport = {
  cameraX: number
  cameraY: number
  cameraZ: number
  targetX: number
  targetY: number
  targetZ: number
}

type ViewportAnim = {
  fromPos: THREE.Vector3
  fromTarget: THREE.Vector3
  toPos: THREE.Vector3
  toTarget: THREE.Vector3
  startTime: number
  duration: number
}

type LoopHandle = {
  requestRender: () => void
  addFrameTask: (task: (now: number) => boolean) => () => void
}

const TRANSITION_MS = 500

function computeFocusBox(model: THREE.Object3D, focusMeshes?: Set<THREE.Mesh>): THREE.Box3 {
  const box = new THREE.Box3()
  if (focusMeshes && focusMeshes.size > 0) {
    for (const m of focusMeshes) box.expandByObject(m)
    if (!box.isEmpty()) return box
  }
  let hasVisible = false
  model.traverse((obj) => {
    if (obj instanceof THREE.Mesh && obj.visible) {
      box.expandByObject(obj)
      hasVisible = true
    }
  })
  if (!hasVisible) box.setFromObject(model)
  return box
}

export function useCameraControl(
  getCamera: () => THREE.PerspectiveCamera | null,
  getControls: () => OrbitControls | null,
  loop: LoopHandle,
) {
  let anim: ViewportAnim | null = null

  loop.addFrameTask((now) => {
    const camera = getCamera()
    const controls = getControls()
    if (!anim || !camera || !controls) return false
    const t = Math.min((now - anim.startTime) / anim.duration, 1)
    const ease = easeInOut(t)
    camera.position.lerpVectors(anim.fromPos, anim.toPos, ease)
    controls.target.lerpVectors(anim.fromTarget, anim.toTarget, ease)
    if (t >= 1) anim = null
    return true
  })

  function applyViewport(v: Viewport, animate = true) {
    const camera = getCamera()
    const controls = getControls()
    if (!camera || !controls) return
    const toPos = new THREE.Vector3(v.cameraX, v.cameraY, v.cameraZ)
    const toTarget = new THREE.Vector3(v.targetX, v.targetY, v.targetZ)
    if (!animate || camera.position.distanceTo(toPos) < 1e-6) {
      anim = null
      camera.position.copy(toPos)
      controls.target.copy(toTarget)
      camera.updateProjectionMatrix()
      controls.update()
      loop.requestRender()
      return
    }
    anim = {
      fromPos: camera.position.clone(),
      fromTarget: controls.target.clone(),
      toPos,
      toTarget,
      startTime: performance.now(),
      duration: TRANSITION_MS,
    }
    loop.requestRender()
  }

  function viewportFraming(box: THREE.Box3, camera: THREE.PerspectiveCamera): Viewport {
    const center = box.getCenter(new THREE.Vector3())
    const offset = computeDefaultOffset(box, camera)
    return {
      cameraX: center.x + offset.x,
      cameraY: center.y + offset.y,
      cameraZ: center.z + offset.z,
      targetX: center.x,
      targetY: center.y,
      targetZ: center.z,
    }
  }

  function refitToBox(box: THREE.Box3) {
    const camera = getCamera()
    if (!camera || box.isEmpty()) return
    applyViewport(viewportFraming(box, camera))
  }

  function fitCamera(model: THREE.Object3D, focusMeshes?: Set<THREE.Mesh>) {
    anim = null
    const camera = getCamera()
    const controls = getControls()
    if (!camera || !controls) return
    const modelBox = new THREE.Box3().setFromObject(model)
    const modelSize = modelBox.getSize(new THREE.Vector3())
    const modelMaxDim = Math.max(modelSize.x, modelSize.y, modelSize.z)
    const focusBox = computeFocusBox(model, focusMeshes)
    const center = focusBox.getCenter(new THREE.Vector3())
    camera.near = Math.max(modelMaxDim * 0.0001, 1e-4)
    camera.far = modelMaxDim * 1000
    camera.position.copy(center).add(computeDefaultOffset(focusBox, camera))
    camera.updateProjectionMatrix()
    controls.target.copy(center)
    controls.update()
    loop.requestRender()
  }

  function resetCamera(findGroup: () => THREE.Group | undefined, focusMeshes?: Set<THREE.Mesh>) {
    const camera = getCamera()
    const model = findGroup()
    if (!camera || !model) return
    applyViewport(viewportFraming(computeFocusBox(model, focusMeshes), camera))
  }

  function currentViewport(): Viewport | null {
    const camera = getCamera()
    const controls = getControls()
    if (!camera || !controls) return null
    return {
      cameraX: camera.position.x,
      cameraY: camera.position.y,
      cameraZ: camera.position.z,
      targetX: controls.target.x,
      targetY: controls.target.y,
      targetZ: controls.target.z,
    }
  }

  return {
    applyViewport,
    refitToBox,
    fitCamera,
    resetCamera,
    currentViewport,
  }
}
